import { useCallback, useState } from 'react';

import { classNames } from '@progress/kendo-react-common';
import { Card, CardBody, CardHeader } from '@progress/kendo-react-layout';
import { Button, Chip } from '@progress/kendo-react-buttons';

import { useAppDispatch, useAppSelector } from '../../../hooks/redux';
import {
  selectSavedFiltersEntries,
  setSavedFilterKey,
} from '../../../store/slices/saved-filters.slice';
import { DateFilterTab, VisualData } from '../../../models/date-filter.model';
import { DateConfig } from '../../../models/saved-filters.model';
import { DATE_FORMAT } from '../../../constants';
import { KWELLO_ICON, KwelloSvgIcon } from '../../icon/KwelloSvgIcon';
import { chipTextArray } from './AppliedFilters';
import {
  AutocompleteFilterOption,
  CommonFilterPayloadAction,
  CommonFiltersItem,
  DatasetID,
  FilterType,
} from '../../../models/filter.model';
import { updateCommonFilters } from '../../../store/slices/common-filters.slice';
import { setAndApplyDateFilter } from '../../../store/slices/date-filter.slice';
import {
  setDeleteFilter,
  setSubscribeFilter,
} from '../../../store/slices/save-criteria-modals.slice';
import { clearChartFilters } from '../../../store/slices/chart-filters.slice';
import { Tooltip } from 'react-tooltip';
import { formatAppDate } from '../../../hooks/utils';
import usePageFilters from 'common/hooks/usePageFilters';

export const convertFiltersToPayloadAction = (
  filters: CommonFiltersItem
): CommonFilterPayloadAction[] => {
  return Object.keys(filters).map((filterKey) => ({
    key: filterKey,
    label: filters[filterKey].label,
    filter: {
      label: filters[filterKey].label,
      value: filters[filterKey].value,
      type: FilterType.AUTOCOMPLETE,
    },
    type: FilterType.AUTOCOMPLETE,
  }));
};

export default function SavedCriteria({ datasetId, onClose }: DatasetID & { onClose: () => void }) {
  const dispatch = useAppDispatch();
  const { pageFilterKeys } = usePageFilters({ datasetId });
  const [expandedKeys, setExpandedKeys] = useState<Set<number>>(new Set());

  const savedFilters = useAppSelector(selectSavedFiltersEntries);

  const getDateString = useCallback((dateConfig: DateConfig) => {
    switch (dateConfig.activeTab) {
      case DateFilterTab.LAST:
        return `Last ${dateConfig.visualRepresentation[0]} ${dateConfig.visualRepresentation[1]}`;
      case DateFilterTab.SINCE:
        return `Since ${formatAppDate(dateConfig.startDate, DATE_FORMAT)}`;
      case DateFilterTab.BETWEEN:
        return `Between ${formatAppDate(dateConfig.startDate, DATE_FORMAT)} - ${formatAppDate(dateConfig.endDate, DATE_FORMAT)}`;
      default:
        return '';
    }
  }, []);

  const toggleFilter = (key: number) => {
    setExpandedKeys((state) => {
      const result = new Set(state);

      if (result.has(key)) {
        result.delete(key);
      } else {
        result.add(key);
      }

      return result;
    });
  };

  const applyFilter = (key: number) => {
    let filter = savedFilters?.find((item) => item.key === key);
    if (filter?.value?.filters && pageFilterKeys?.length > 0) {
      const onlyAttributes = Object.keys(filter.value.filters).filter((key) =>
        key.includes('attributes_')
      );

      const filteredEntries = Object.entries(filter.value.filters).filter(([key]) => {
        const normalized = key.startsWith('attributes_')
          ? `Attributes['${key.replace('attributes_', '')}']`
          : key;
        return pageFilterKeys.includes(normalized) || onlyAttributes.includes(key);
      });

      filter = {
        ...filter,
        value: {
          ...filter.value,
          filters: Object.fromEntries(filteredEntries),
        },
      };
    }

    if (filter) {
      const filters = convertFiltersToPayloadAction(filter.value.filters);

      const startDate = filter.value.dateConfig.startDate;
      const endDate = filter.value.dateConfig.endDate;
      const visualData = filter.value.dateConfig.visualRepresentation as VisualData;
      const visualType = filter.value.dateConfig.activeTab;
      dispatch(updateCommonFilters({ datasetId, filters }));
      dispatch(
        setAndApplyDateFilter({
          datasetId,
          startDate: new Date(startDate),
          endDate: new Date(endDate),
          visualData,
          visualType,
        })
      );
      dispatch(clearChartFilters({ datasetId }));
      dispatch(setSavedFilterKey({ datasetId, value: key }));
      onClose();
    }
  };

  return (
    <div>
      {savedFilters?.map((filter) => {
        const dateString = getDateString(filter.value.dateConfig);
        const keys = Object.keys(filter.value.filters || {});
        const filters = filter.value.filters;
        const isInvalid = false;

        return (
          <Card key={filter.key} className="saved-filters-card">
            <CardHeader className={classNames('saved-filters-card-header', { invalid: isInvalid })}>
              <div
                className="saved-filters-card-header-title"
                onClick={() => toggleFilter(filter.key)}
              >
                {filter.value.name}
                <KwelloSvgIcon
                  icon={KWELLO_ICON.CARRET_DOWN}
                  className={classNames('k-icon k-svg-icon k-button-icon', {
                    up: expandedKeys.has(filter.key),
                  })}
                />
              </div>
              <div className="saved-filters-card-header-actions">
                {!isInvalid && (
                  <span data-tooltip-id="notification-button">
                    <Button
                      rounded="full"
                      themeColor="secondary"
                      size="small"
                      className="notification"
                      fillMode="flat"
                      onClick={() => {
                        dispatch(setSubscribeFilter(filter.key));
                      }}
                      disabled
                    >
                      <KwelloSvgIcon
                        icon={KWELLO_ICON.NOTIFICATION_ICON}
                        className="k-icon k-svg-icon k-button-icon"
                      />
                    </Button>
                    <Tooltip
                      id="notification-button"
                      place="top"
                      positionStrategy={'fixed'}
                      style={{ fontSize: '0.8rem' }}
                    >
                      Coming soon ...
                    </Tooltip>
                  </span>
                )}
                {!expandedKeys.has(filter.key) && !isInvalid && (
                  <Button
                    rounded="full"
                    themeColor="secondary"
                    size="small"
                    className="apply"
                    onClick={() => applyFilter(filter.key)}
                  >
                    <KwelloSvgIcon
                      icon={KWELLO_ICON.ARROW_RIGHT}
                      className="k-icon k-svg-icon k-button-icon"
                    />
                  </Button>
                )}

                {!expandedKeys.has(filter.key) && isInvalid && (
                  <KwelloSvgIcon
                    icon={KWELLO_ICON.WARNING_ICON}
                    className="k-icon k-svg-icon k-button-icon"
                  />
                )}
              </div>
            </CardHeader>
            {expandedKeys.has(filter.key) && (
              <CardBody
                className={classNames('saved-filters-card-body', {
                  expanded: expandedKeys.has(filter.key),
                })}
                style={{ overflow: 'hidden' }}
              >
                <div className="saved-filters">
                  <>
                    <div className="saved-filters-chip-list">
                      <div className="saved-filter-children" title={dateString}>
                        <Chip fillMode={'outline'}>
                          <>
                            <KwelloSvgIcon
                              icon={KWELLO_ICON.CALENDAR_ICON}
                              className="k-icon k-svg-icon k-button-icon"
                            />
                            <span className="k-chip-label">
                              <span>Date</span> <b className="color-primary">{dateString}</b>
                            </span>
                          </>
                        </Chip>
                      </div>
                      {keys.map((filterKey: string) => {
                        const currentFilter = filters && filters[filterKey];
                        const textArray = chipTextArray(currentFilter?.value);
                        return (
                          <div
                            key={filterKey}
                            className="saved-filter-children"
                            title={currentFilter?.value
                              .map(({ name }: AutocompleteFilterOption) => name)
                              .join(', ')}
                          >
                            <Chip fillMode={'outline'}>
                              <span className="k-chip-label">{currentFilter?.label}&nbsp;</span>
                              <b className={'color-primary'}>
                                {textArray.join(textArray.length > 1 ? ', ' : ' ')}
                              </b>
                            </Chip>
                          </div>
                        );
                      })}
                    </div>
                  </>
                </div>
                <div className="saved-filters-card-body-actions">
                  <Button
                    fillMode="flat"
                    themeColor="secondary"
                    size="small"
                    className="delete"
                    onClick={() => {
                      dispatch(setDeleteFilter(filter.key));
                    }}
                  >
                    <KwelloSvgIcon
                      icon={KWELLO_ICON.TRASH_BIN_ICON}
                      className="k-icon k-svg-icon k-button-icon"
                    />
                  </Button>
                  {!isInvalid && (
                    <Button
                      rounded="full"
                      themeColor="secondary"
                      size="small"
                      className="apply"
                      onClick={() => applyFilter(filter.key)}
                    >
                      Apply
                      <KwelloSvgIcon
                        icon={KWELLO_ICON.ARROW_RIGHT}
                        className="k-icon k-svg-icon k-button-icon"
                      />
                    </Button>
                  )}
                  {isInvalid && (
                    <div className="warning">
                      <KwelloSvgIcon
                        icon={KWELLO_ICON.WARNING_ICON}
                        className="k-icon k-svg-icon k-button-icon"
                      />
                      Incompatible Criteria
                    </div>
                  )}
                </div>
              </CardBody>
            )}
          </Card>
        );
      }) ?? null}
    </div>
  );
}
